import { ArrowPathRoundedSquareIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { CompanyIntegrationDialog } from "../../../components/dashboard/company/integrations/dialog";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  deleteTripletexSubscriptions,
  fetchCompany,
  syncModule,
} from "../../../api/company";
import SimplePrompt from "../../../components/utils/simplePrompt";
import { mapErrors } from "../../../components/Utils";
import { useTranslation } from "react-i18next";
import SyncDialog from "../../../components/dashboard/company/integrations/sync-dialog";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PMTabs } from "../../../components/utils/PMTabs";
import ServicesTab from "../../../components/dashboard/company/integrations/ServicesTab";
import OauthAppsTab from "../../../components/dashboard/company/integrations/OauthAppsTab";
import WebhooksTab from "../../../components/dashboard/company/integrations/WebhooksTab";

const TAB_KEYS = ["services", "apps", "webhooks"];

export const CompanyIntegrations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [company, setCompany] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const selectedTab = useMemo(() => {
    const index = TAB_KEYS.indexOf(searchParams.get("tab"));
    return index >= 0 ? index : 0;
  }, [searchParams]);

  const handleTabChange = (value) => {
    setSearchParams({ tab: TAB_KEYS[Number(value)] || TAB_KEYS[0] }, { replace: true });
  };

  const getCompany = () => {
    setIsLoading(true);
    fetchCompany()
      .then((res) => {
        setIsLoading(false);
        setCompany(res.data?.data || res.data);
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    getCompany();
  }, []);

  const [syncPromptState, setSyncPromptState] = useState(false);
  const [deleteSubscriptionPromptOpen, setDeleteSubscriptionPromptOpen] = useState(false);
  const [syncState, setSyncState] = useState({
    isLoading: false,
    error: false,
    errors: [],
    updated: 0,
    created: 0,
    found: 0,
  });
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncModules, setSyncModules] = useState({});

  const runOrderSync = () => {
    const params = {
      syncUsers: true,
      syncCustomers: true,
      syncDeliveryAddresses: true,
      syncDepartments: true,
      syncContacts: true,
      syncProjects: true,
      syncProducts: false,
      force: true,
    };
    return syncModule(params)
      .then((res) => {
        setSyncState((prevState) => ({
          ...prevState,
          isLoading: false,
          updated: res?.data?.updated,
          created: res?.data?.created,
          found: res?.data?.found,
        }));
        toast.success(
          `${syncState.updated} was updated out of ${syncState.found} - ${syncState.created} was created`
        );
        return res;
      })
      .catch((err) => {
        setSyncState((prevState) => ({
          ...prevState,
          isLoading: false,
          updated: 0,
          created: 0,
          found: 0,
        }));
        return err;
      });
  };

  const headerActions =
    selectedTab === 0
      ? [
          {
            component: null,
            label: t("company.integrations.sync_tripletex"),
            description: "",
            path: null,
            action: () => setSyncDialogOpen(true),
            icon: ArrowPathRoundedSquareIcon,
            textColor: "text-teal-700",
            bgColor: "bg-white",
            hidden: false,
            menu: true,
          },
          {
            component: null,
            label: t("company.integrations.delete_tripletex_subscriptions"),
            description: "",
            path: null,
            action: () => setDeleteSubscriptionPromptOpen(true),
            icon: ArrowPathRoundedSquareIcon,
            textColor: "text-teal-700",
            bgColor: "bg-white",
            hidden: false,
            menu: true,
          },
        ]
      : [];

  return (
    <>
      <DashboardHeader
        subtitle={t("company.integrations.subtitle")}
        actions={headerActions}
        title={t("company.integrations.title")}
        meta={[]}
      >
        <div className="mx-auto mt-8 max-w-7xl px-4 sm:px-6">
          <div className="mb-6">
            <PMTabs
              tabs={[
                { name: t("company.integrations.tabs.services") },
                { name: t("company.integrations.tabs.apps") },
                { name: t("company.integrations.tabs.webhooks") },
              ]}
              selectedTab={selectedTab}
              setSelectedTab={handleTabChange}
            />
          </div>
          {selectedTab === 0 && (
            <ServicesTab
              company={company}
              onOpen={(item) => {
                setSelectedIntegration(item);
                setIntegrationDialogOpen(true);
              }}
            />
          )}
          {selectedTab === 1 && <OauthAppsTab />}
          {selectedTab === 2 && <WebhooksTab />}
        </div>
      </DashboardHeader>
      {selectedIntegration && (
        <CompanyIntegrationDialog
          company={company}
          integration={selectedIntegration}
          open={integrationDialogOpen}
          isLoading={isLoading}
          onLoading={(state) => setIsLoading(state)}
          onClose={() => {
            setIntegrationDialogOpen(false);
            setSelectedIntegration(null);
          }}
          onRefresh={(data) => setCompany(data)}
        />
      )}
      <SimplePrompt
        title={t("company.integrations.sync_products_title")}
        description={t("company.integrations.sync_products_body")}
        open={syncPromptState}
        onClose={() => setSyncPromptState(false)}
        onAccept={runOrderSync}
      />
      <SimplePrompt
        title={t("company.integrations.delete_tripletex_subscriptions")}
        description={t("company.integrations.delete_tripletex_body")}
        open={deleteSubscriptionPromptOpen}
        onClose={() => setDeleteSubscriptionPromptOpen(false)}
        onAccept={() => {
          toast.promise(deleteTripletexSubscriptions(), {
            loading: t("Syncing...."),
            success: (res) => {
              if (res.data.errors) throw new Error(mapErrors(res.data.errors));
              return t("company.integrations.sync_success");
            },
            error: t("Something went wrong. Could not save data!"),
          });
        }}
      />
      <SyncDialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        modules={syncModules}
        onModulesChange={setSyncModules}
        isLoading={syncState.isLoading}
      />
    </>
  );
};
